import { ChangeDetectionStrategy, Component, computed, HostListener, signal } from '@angular/core';

import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { combineLatest, Observable, of } from 'rxjs';
import { map, switchMap, take, takeUntil } from 'rxjs/operators';
import { BaseContainer } from '../../../../core/common/containers/base.abstract.container';
import {
    BaseNestedFieldDefinition,
    DottedKey,
    FieldType
} from '../../../../core/field-definitions/entities/field-definition.entity';
import { ExtendedBaseProfileField } from '../../../../core/profiles/entities/extended-profile-field.entity';
import { EmptyStructuredQuery } from '../../../../core/query/entities/empty-structured-query.constant';
import { LogicalOperand, Operand } from '../../../../core/query/entities/operand.entity';
import { AdvancedSearch, AdvancedSearchTerm } from '../../../../core/query/entities/structured-query.entity';
import { DialogConfig } from '../../../../dynamic-dialog/entities/dialog-config.entity';
import { DialogRef } from '../../../../dynamic-dialog/entities/dialog-ref.class';
import { ShActionItemComponent } from '../../../../elements/components/action-item/action-item.component';
import {
    ActionItem,
    ActionItemIconPosition,
    ActionItemStyle
} from '../../../../elements/components/action-item/entities/action-item.entity';
import { ShIconModule } from '../../../../elements/components/icon/icon.module';
import { FormSelectOption } from '../../../../form-controls/entities/form-select-option.entity';
import { FieldDefinitionToProfileField } from '../../../../profile-form/helpers/to-profile-field/to-profile-field.helper';
import {
    dateRangeStringToRangeValue,
    rangeValueToString
} from '../../../../saved-queries/helpers/convert-structured-query.helper';
import { ShAdvancedSearchGroupComponent } from '../../components/advanced-search-group/advanced-search-group.component';
import { AdvancedSearchGroup } from '../../entities/advanced-search-group.entity';
import { AdvancedSearchRow } from '../../entities/advanced-search-row.entity';
import { AdvancedSearchDialogTranslations } from '../../entities/i18n/advanced-search-translations.entity';
import { BaseAdvancedSearchSandbox } from '../../sandbox/advanced-search.sandbox';

@Component({
    standalone: true,
    selector: 'sh-advanced-search-dialog',
    templateUrl: './advanced-search-dialog.container.html',
    styleUrls: ['./advanced-search-dialog.container.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        FormsModule,
        ShActionItemComponent,
        ShIconModule,
        ShAdvancedSearchGroupComponent,
        TranslateModule
    ]
})
export class ShAdvancedSearchDialogContainerComponent extends BaseContainer {
    globalLogicalOperand: LogicalOperand = LogicalOperand.And;
    addGroupActionItem: ActionItem;

    // Options
    // TODO: Move generic translations to here
    readonly i18n: AdvancedSearchDialogTranslations;
    readonly matchingOptions: FormSelectOption[] = [
        { label: this.translateService.translate('advanced-search.operation.and'), value: LogicalOperand.And },
        {
            label: this.translateService.translate('advanced-search.operation.or'),
            value: LogicalOperand.Or
        }
    ];

    $filterGroups = signal(EmptyStructuredQuery.AdvancedSearch.Groups as AdvancedSearchGroup[]);

    public profileFields$: Observable<ExtendedBaseProfileField[]>;
    public missingProfileFields$: Observable<ExtendedBaseProfileField[]>;
    public fieldTypeOptions: FormSelectOption[] = [];

    public mergedProfileFieldsStream$: Observable<ExtendedBaseProfileField[]>;

    private profileFields: ExtendedBaseProfileField[];

    readonly $dialogActionItems = computed(() => this.getDialogActionItems(this.$filterGroups()));

    constructor(
        private readonly sandbox: BaseAdvancedSearchSandbox<BaseNestedFieldDefinition>,
        public config: DialogConfig,
        public dialog: DialogRef<AdvancedSearch>
    ) {
        super();
        this.i18n = this.config.data.i18n;

        this.initProfileFieldsStream();
        this.initData();
        this.initGroupActionItem();
    }

    @HostListener('document:keydown.enter', ['$event'])
    handleEnter(event: { target: HTMLInputElement }) {
        this.applyChanges(this.$filterGroups());
    }

    private initProfileFieldsStream(): void {
        this.profileFields$ = this.sandbox.getProfileFieldsStream(this.config?.data?.tag);

        // Can occur when quick-searching on a field etc that a field in the query is not present in the profile
        this.missingProfileFields$ = this.profileFields$.pipe(
            switchMap(profileFields => this.getMissingProfileFields(profileFields))
        );
    }

    private initData(): void {
        this.mergedProfileFieldsStream$ = combineLatest([this.profileFields$, this.missingProfileFields$]).pipe(
            map(([profileFields, missingProfileFields]) => [...profileFields, ...missingProfileFields])
        );

        this.mergedProfileFieldsStream$.pipe(take(1), takeUntil(this.destroy$)).subscribe(profileFields => {
            // Add missing profileFields to types
            this.profileFields = profileFields;
            this.fieldTypeOptions = this.getFieldTypeOptions(this.profileFields);
            this.setInitialGroups();
        });
    }

    private setInitialGroups(): void {
        const advancedSearch: AdvancedSearch | undefined = this.advancedSearch;

        if (!advancedSearch) {
            // Create empty row
            this.$filterGroups.set([this.newFilterGroup()]);
            return;
        }

        this.globalLogicalOperand = advancedSearch.LogicalOperand;

        const convertedGroups: AdvancedSearchGroup[] = advancedSearch.Groups.map(group => ({
            ...group,
            Terms: this.toAdvancedSearchRows(group.Terms ?? [])
        }));

        if (convertedGroups.length === 0) {
            // Create empty row
            this.$filterGroups.set([this.newFilterGroup()]);
            return;
        }

        this.$filterGroups.set(convertedGroups);
    }

    // #region Getters
    private get advancedSearch(): AdvancedSearch | undefined {
        return this.config.data?.advancedSearch;
    }
    // #endregion

    // #region Helpers
    private getFieldTypeOptions(profileFields: ExtendedBaseProfileField<BaseNestedFieldDefinition>[]) {
        return this.sandbox.profileFieldsToTypeSelectOptions(profileFields);
    }

    private getMissingProfileFields(
        profileFields: ExtendedBaseProfileField<BaseNestedFieldDefinition>[]
    ): Observable<ExtendedBaseProfileField<BaseNestedFieldDefinition>[]> {
        const advancedSearch: AdvancedSearch = this.advancedSearch ?? EmptyStructuredQuery.AdvancedSearch;
        const advancedSearchKeys: DottedKey[] = advancedSearch.Groups.flatMap(group =>
            (group.Terms ?? []).map(term => term.DottedKey as DottedKey)
        );
        const advancedSearchDottedKeys: DottedKey[] = advancedSearchKeys.filter(key => key.includes('.'));
        const advancedSearchFlatKeys: string[] = advancedSearchKeys.filter(key => !key.includes('.'));

        const profileDottedKeys: DottedKey[] = profileFields.map(profileField => profileField.DottedKey);
        const profileFlatKeys: string[] = profileFields.map(profileField => profileField.FieldDefinition.FlatKey);

        const missingDottedKeys: DottedKey[] = advancedSearchDottedKeys.filter(
            advancedSearchDottedKey => !profileDottedKeys.includes(advancedSearchDottedKey)
        );
        const missingFlatKeys: string[] = advancedSearchFlatKeys.filter(
            advancedSearchFlatKey => !profileFlatKeys.includes(advancedSearchFlatKey)
        );

        if (missingDottedKeys.length === 0 && missingFlatKeys.length === 0) {
            return of([]);
        }

        const dottedKeyMissingFields$: Observable<BaseNestedFieldDefinition[]> =
            this.sandbox.getFieldDefinitions(missingDottedKeys);

        const flatKeyMissingFields$: Observable<BaseNestedFieldDefinition[]> =
            this.sandbox.getFieldDefinitionsByFlatKeys(missingFlatKeys);

        return combineLatest([dottedKeyMissingFields$, flatKeyMissingFields$]).pipe(
            map(([defs1, defs2]) => defs1.concat(defs2)),
            map(definitions => definitions.map(FieldDefinitionToProfileField))
        );
    }

    // #endregion

    // #region FilterGroups
    private initGroupActionItem(): void {
        this.addGroupActionItem = {
            label: this.translateService.translate('advanced-search.filter-groups.add'),
            icon: 'plus',
            iconPosition: ActionItemIconPosition.Left,
            eventListener: () => this.addGroup()
        };
    }

    addGroup(): void {
        this.$filterGroups.update(groups => {
            groups.push(this.newFilterGroup());
            return groups;
        });
    }

    removeGroup(index: number): void {
        this.$filterGroups.update(groups => {
            groups.splice(index, 1);

            if (groups.length === 0) {
                return [this.newFilterGroup()];
            }
            return groups;
        });
    }

    private newFilterGroup(): AdvancedSearchGroup {
        return {
            LogicalOperand: LogicalOperand.And,
            Terms: [this.newFilter()]
        };
    }
    // #endregion

    // #region Filters
    filtersChanged(groupIndex: number, filters: AdvancedSearchRow[]): void {
        this.$filterGroups.update(groups => {
            const updatedGroups = groups;
            updatedGroups[groupIndex].Terms = filters;

            return updatedGroups;
        });
    }

    private newFilter(): AdvancedSearchRow {
        return {
            Field: null,
            Value: null,
            Operand: Operand.Include
        };
    }
    // #endregion

    // #region Dialog ActionItems
    private getDialogActionItems(filterGroups: AdvancedSearchGroup[]): ActionItem[] {
        const searchActionItem: ActionItem = {
            label: this.i18n.actions.search.label,
            style: ActionItemStyle.Primary,
            eventListener: () => this.applyChanges(filterGroups)
        };

        const cancelActionItem: ActionItem = {
            label: this.i18n.actions.cancel.label,
            style: ActionItemStyle.Secondary,
            eventListener: () => this.closeDialog()
        };

        return [searchActionItem, cancelActionItem];
    }

    private applyChanges(filterGroups: AdvancedSearchGroup[]): void {
        const convertedGroups = filterGroups.map(group => ({
            ...group,
            Terms: this.toAdvancedSearchTerms(group.Terms)
        }));

        const advancedSearch: AdvancedSearch = {
            LogicalOperand: this.globalLogicalOperand,
            Groups: convertedGroups
        };

        this.dialog.close(advancedSearch);
    }

    private closeDialog(): void {
        this.dialog.close();
    }
    // #endregion

    // #region Convert AdvancedSearchTerms to AdvancedSearchRows
    private toAdvancedSearchRows(advancedSearchTerms: AdvancedSearchTerm[]): AdvancedSearchRow[] {
        return advancedSearchTerms
            .flatMap(({ DottedKey, Values, RangeValues, Operand }) => {
                // Create separate row for each value
                if (RangeValues) {
                    return RangeValues.map(value => ({
                        Field: this.getProfileField(DottedKey),
                        Value: rangeValueToString(value),
                        Operand
                    }));
                }

                return (Values ?? []).map(value => {
                    const field = this.getProfileField(DottedKey);

                    return {
                        Field: field,
                        Value: this.convertLegacyValues(value, field),
                        Operand
                    };
                });
            })
            .filter(advancedSearchRow => this.assertProfile(advancedSearchRow.Field));
    }

    private getProfileField(dottedKey: string): ExtendedBaseProfileField<BaseNestedFieldDefinition> | undefined {
        // Legacy check
        if (!dottedKey.includes('.')) {
            return this.profileFields.find(field => field.FieldDefinition.FlatKey === dottedKey);
        }
        return this.profileFields.find(field => field.FieldDefinition.DottedKey === dottedKey);
    }

    // ED-4510: Necessary to convert 1/0 to true/false since DAV still loads saved facet searches to advanced search
    // Can be removed after DAV split-off
    private convertLegacyValues(
        value: string,
        field: ExtendedBaseProfileField<BaseNestedFieldDefinition> | undefined
    ): string {
        if (field?.FieldDefinition.Type === FieldType.BooleanField) {
            return value === '1' ? 'true' : value === '0' ? 'false' : value;
        }

        return value;
    }

    private assertProfile(
        profile: ExtendedBaseProfileField<BaseNestedFieldDefinition> | undefined
    ): profile is ExtendedBaseProfileField<BaseNestedFieldDefinition> {
        return (profile as ExtendedBaseProfileField<BaseNestedFieldDefinition>) !== undefined;
    }
    // #endregion

    // #region Convert AdvancedSearchRows to AdvancedSearchTerms
    public toAdvancedSearchTerms(rows: AdvancedSearchRow[]): AdvancedSearchTerm[] {
        const terms: AdvancedSearchTerm[] = rows
            .map(row => this.mapAdvancedSearchRowToAdvancedSearchTerm(row))
            .filter((term): term is AdvancedSearchTerm => !!term);

        return terms;
    }

    protected mapAdvancedSearchRowToAdvancedSearchTerm(row: AdvancedSearchRow): AdvancedSearchTerm | null {
        if (!row.Field || !row.Value || !row.Operand) {
            return null;
        }

        return row.Field.FieldDefinition.Type === FieldType.DateField
            ? {
                  DottedKey: row.Field.DottedKey,
                  Operand: row.Operand,
                  RangeValues: [dateRangeStringToRangeValue(row.Value?.toString())]
              }
            : {
                  DottedKey: row.Field.DottedKey,
                  Operand: row.Operand,
                  Values: [row.Value?.toString() || '']
              };
    }
    // #endregion
}
